import { useArena } from '../state/ArenaContext.tsx';
import type { AppRoute } from '../state/types.ts';
import { Button, cx } from '../components/ui/primitives.tsx';
import { OpticalTrack } from '../components/system/OpticalTrack.tsx';
import { roomLink } from './identity.ts';

const ROUTE_LABEL: Record<AppRoute, string> = {
  home: '大厅',
  arena: '作战台',
  coach: '教练台',
  review: '复盘台',
  settings: '设置',
};

function BrandMark() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" className="text-white" aria-hidden="true">
      <circle cx="12" cy="12" r="8.6" stroke="currentColor" strokeOpacity="0.55" strokeWidth="1.1" strokeDasharray="4 2.6" />
      <circle cx="12" cy="12" r="2.6" fill="currentColor" />
      <path d="M12 1.4v4.2M12 18.4v4.2M1.4 12h4.2M18.4 12h4.2" stroke="currentColor" strokeWidth="1.1" />
      <path d="M4.6 4.6l2 2M17.4 17.4l2 2M19.4 4.6l-2 2M6.6 17.4l-2 2" stroke="currentColor" strokeOpacity="0.4" strokeWidth="1" />
    </svg>
  );
}

export function TopBar({ compact = false }: { compact?: boolean }) {
  const { state, actions } = useArena();
  const isTeacher = state.self?.role === 'TEACHER';
  const routes: AppRoute[] = isTeacher ? ['coach', 'review'] : ['arena', 'review'];
  const stageBudgetMs = state.stage
    ? (state.stage.budgets.AFF + state.stage.budgets.NEG) * 1000
    : 0;
  const stageRemainingMs = state.remainingMs.AFF + state.remainingMs.NEG;
  const stageProgress =
    stageBudgetMs > 0 ? Math.max(0, Math.min(1, 1 - stageRemainingMs / stageBudgetMs)) : 0;

  const copyRoomLink = async () => {
    if (!state.roomId) return;
    try {
      await navigator.clipboard.writeText(roomLink(state.roomId));
    } catch {
      /* ignore */
    }
  };

  return (
    <header className="sticky top-0 z-30 border-b border-white/[0.055] bg-[#040507]/72 backdrop-blur-2xl">
      <div
        className={cx(
          'mx-auto flex max-w-[1520px] items-center justify-between gap-5 px-5',
          compact ? 'h-12' : 'h-14',
        )}
      >
        <div className="flex min-w-0 items-center gap-5">
          <button
            type="button"
            className="group flex shrink-0 items-center gap-2.5 text-left"
            data-testid="brand-home"
            onClick={() => actions.setRoute('home')}
            title="返回大厅"
          >
            <span className="grid h-8 w-8 place-items-center rounded-lg bg-white/[0.035] shadow-[inset_0_1px_0_0_rgba(255,255,255,0.16),inset_0_0_0_1px_rgba(255,255,255,0.05)] transition group-hover:bg-white/[0.06]">
              <BrandMark />
            </span>
            <span className="leading-tight">
              <span className="block text-[13px] font-semibold tracking-[0.08em] text-metal">
                纵横演武场
              </span>
              <span className="micro-label mt-0.5 block">ZONGHENG ARENA</span>
            </span>
          </button>

          {state.roomId && (
            <>
              <span className="hairline-v hidden h-6 sm:block" />
              <button
                type="button"
                onClick={copyRoomLink}
                title="复制房间链接"
                data-testid="room-code"
                className="hidden items-center gap-2 rounded-md bg-white/[0.035] px-2.5 py-1 text-[11px] text-slate-400 shadow-[inset_0_1px_0_0_rgba(255,255,255,0.1),inset_0_0_0_1px_rgba(255,255,255,0.04)] transition hover:bg-white/[0.06] hover:text-slate-200 sm:flex"
              >
                <span className="micro-label">ROOM</span>
                <span className="mono tracking-[0.22em] text-team-aff">{state.roomId}</span>
              </button>
              {state.stage && (
                <span className="hidden items-center gap-2 text-[11px] text-slate-400 xl:flex">
                  <span className="status-dot status-dot--live" />
                  <span className="mono uppercase tracking-[0.14em]">
                    {String(state.stage.index).padStart(2, '0')}
                  </span>
                  <span className="text-slate-500">/</span>
                  <span className="text-slate-200">{state.stage.name}</span>
                </span>
              )}
            </>
          )}
        </div>

        <div className="flex items-center gap-2">
          {state.roomId && (
            <nav className="hidden items-center gap-0.5 rounded-lg bg-white/[0.025] p-0.5 shadow-[inset_0_1px_0_0_rgba(255,255,255,0.08),inset_0_0_0_1px_rgba(255,255,255,0.03)] sm:flex">
              {routes.map((route) => (
                <button
                  key={route}
                  type="button"
                  data-testid={`nav-${route}`}
                  onClick={() => actions.setRoute(route)}
                  className={cx(
                    'rounded-md px-3 py-1 text-[11px] font-medium transition-colors',
                    state.route === route
                      ? 'bg-white/[0.09] text-white shadow-[inset_0_1px_0_0_rgba(255,255,255,0.14)]'
                      : 'text-slate-500 hover:text-slate-200',
                  )}
                >
                  {ROUTE_LABEL[route]}
                </button>
              ))}
            </nav>
          )}
          <ConnectionBadge />
          <button
            type="button"
            data-testid="open-settings"
            onClick={() => actions.setRoute('settings')}
            title="统一设置"
            className={cx(
              'grid h-7 w-7 place-items-center rounded-md text-slate-400 transition hover:bg-white/[0.06] hover:text-slate-100',
              state.route === 'settings' && 'bg-white/[0.08] text-white',
            )}
            aria-label="打开设置"
          >
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <circle cx="12" cy="12" r="3.2" stroke="currentColor" strokeWidth="1.5" />
              <path d="M12 2.8v2.1M12 19.1v2.1M2.8 12h2.1M19.1 12h2.1M5.5 5.5l1.5 1.5M17 17l1.5 1.5M18.5 5.5 17 7M7 17l-1.5 1.5" stroke="currentColor" strokeWidth="1.3" />
            </svg>
          </button>
          {state.roomId && (
            <Button size="sm" variant="ghost" onClick={() => actions.leaveRoom()} testId="leave-room">
              离开
            </Button>
          )}
        </div>
      </div>

      {state.roomId && (
        <div className="mx-auto flex max-w-[1520px] items-center gap-4 px-5 pb-2.5">
          <OpticalTrack
            currentIndex={state.stageIndex}
            completedStages={state.completedStages}
            progress={stageProgress}
            className="min-w-0 flex-1"
          />
          <span className="hidden shrink-0 text-[10px] text-slate-500 md:block">
            <span className="micro-label">CONNECTION</span>
          </span>
        </div>
      )}
    </header>
  );
}

function ConnectionBadge() {
  const { state } = useArena();
  const live = state.connection === 'connected';
  const warning = state.connection === 'connecting' || state.connection === 'reconnecting';
  const failed = state.connection === 'error';
  const label = live
    ? 'P2P 已连接'
    : state.connection === 'connecting'
      ? '连接中…'
      : state.connection === 'reconnecting'
        ? '重连中…'
        : failed
          ? '连接异常'
          : '未连接';
  return (
    <span
      data-testid="connection-badge"
      title={state.connectionDetail}
      className="inline-flex items-center gap-2 rounded-md bg-white/[0.025] px-2.5 py-1 text-[10px] text-slate-400 shadow-[inset_0_1px_0_0_rgba(255,255,255,0.08),inset_0_0_0_1px_rgba(255,255,255,0.03)]"
    >
      <span
        className={cx(
          'h-1.5 w-1.5 rounded-full',
          live
            ? 'bg-white shadow-[0_0_10px_rgba(255,255,255,0.9),0_0_22px_rgba(125,211,252,0.55)]'
            : failed
              ? 'bg-[#FB7185] shadow-[0_0_10px_rgba(251,113,133,0.7)]'
              : warning
                ? 'bg-[#FCD34D] shadow-[0_0_10px_rgba(252,211,77,0.6)]'
                : 'bg-slate-600',
        )}
      />
      {label}
    </span>
  );
}

export function Toasts() {
  const { state, actions } = useArena();
  if (state.toasts.length === 0) return null;
  const tones: Record<string, string> = {
    info: 'text-slate-200',
    success: 'text-[#DCFCE7]',
    warning: 'text-[#FDE68A]',
    error: 'text-[#FECDD3]',
  };
  return (
    <div className="pointer-events-none fixed bottom-5 right-5 z-50 flex w-80 flex-col gap-2">
      {state.toasts.map((toast) => (
        <button
          key={toast.id}
          type="button"
          onClick={() => actions.dismissToast(toast.id)}
          className={cx(
            'lens pointer-events-auto px-3.5 py-2.5 text-left text-[11px] leading-relaxed backdrop-blur-2xl',
            tones[toast.kind],
          )}
        >
          <span className="mr-2 inline-block h-1 w-1 rounded-full bg-current align-middle shadow-[0_0_8px_currentColor]" />
          {toast.text}
        </button>
      ))}
    </div>
  );
}
